/**
 * 63.14 step 2b, the terraces' build (ARCHITECTURE §5.22 "Terracing under pre-generated bases"; DECISIONS 205 (1); the
 * owner on the sheet: "prefer flatter land but terrace when necessary"): the setup pass that lays a terrace under each
 * pre-generated base whose footing is walkable (core/stands.ts footingOf: a building's plinth, the pad's deck) where the
 * natural ground under its footprint rises more than TERRACE.minRisePeople — flat ground keeps the plinth's bedding
 * (67.52) — and no more than TERRACE.maxStepPeople (over that the site should split into pads along the contour: the
 * sites pass's work, later; until then the plinth stands in). The pad's level is the **median** of the natural ground
 * sampled over the footprint, the level that moves the least earth (Σ |level − natural|). Each terrace is a record in the
 * terrain log (TerrainEdit `kind: 'terrace'`, applied through applyTerrainEdit: the record and its apron), written at setup
 * in the structures' order before any battle edit, so a replay and a sector re-fight lay it again (R13). Behind
 * `GameConfig.terraces` (the Lab's Terraces row beside Modular buildings): off, no record, no apron, every byte as before.
 * The seat (buildBaseLift) then finds the pad's level under the whole footprint and the stand's deck is that level.
 */
import type { GameState, PlanetState, TerrainEdit } from '../types';
import type { Terrace } from '../types/ground';
import { TERRACE, terraceCentre, terraceReach, padSnapshots } from './terraceGround';
import { applyTerrainEdit } from './terrainEdits';
import { personSurfaceRadius, groundAt, type GroundLevel } from './fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { footingOf } from './stands';
import { structureFrame } from './hits';
import { vecNormalize } from '../math';
import { getSurfaceRadius } from './surface';
import { layoutFor } from './modules';
import { OUTSIDE, roomGraph } from './roomGraph';
import { MODULE_CELL, MODULE_SPAN } from '../constants/modules';
import type { StructureState } from '../types';

/** The log's record of a terrace laid at setup. */
export function terraceEdit(planetIndex: number, planet: PlanetState, t: Terrace): TerrainEdit {
  return { kind: 'terrace', planetIndex, planetId: planet.id, point: terraceCentre(planet, t), radius: terraceReach(t), depth: 0, terrace: t };
}

/**
 * 69.42 (the terraces' planned "vehicle ramp at the gate"): the ramps a terrace `t` takes for the building `s` on it — each of
 * its layout's vehicle gates (`gate`, `gateWide`) onto the outside, on the pad's edge the gate faces (its outward normal in the
 * pad's frame), where the gate stands along that edge, the gate's half opening and TERRACE.rampMarginPeople wide; and as long
 * as the ground straight out from it stands above the ramp's line — to the first quarter person past it where it does not, TERRACE.rampReachBands
 * bands at most (rampFit; 71.7 (2): steeper where a hill outruns the line, and refitted by settleRamps) — read on the planet with `t` laid (its coarse apron moves the mesh round the pad: before it, the ground behind
 * cv-1's pool's back gate stood under the line and that gate took none) but without `t`'s own rule. Where the ground there
 * never stands above it (a fill bank, the hardstand: no cut to ramp), a ramp down where it falls under the fill's line (the
 * owner's stuck troops, Nov 2: rampFit's mirror); none on the hardstand, none for a building with no layout or no gate.
 */
export function gateRamps(planet: PlanetState, t: Terrace, s: Pick<StructureState, 'type' | 'surfaceDir' | 'yaw' | 'builder' | 'stacks'>, config: GameState['config'], ps: ReturnType<typeof personSetOf>): NonNullable<Terrace['ramps']> {
  const layout = layoutFor(s, config);
  if (!layout) return [];
  const person = PERSON_BODY_HEIGHT * ps.k;
  const laid = applyTerrainEdit(planet, { kind: 'terrace', planetIndex: 0, planetId: planet.id, point: terraceCentre(planet, t), radius: terraceReach(t), depth: 0, terrace: t });
  const ground = { ...laid, terraces: (laid.terraces ?? []).filter(x => x.id !== t.id) };   // (its apron, not its rule)
  const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s as StructureState, up), R = planet.radius;
  const d = t.dir, f = t.facing, rx = f.y * d.z - f.z * d.y, ry = f.z * d.x - f.x * d.z, rz = f.x * d.y - f.y * d.x;   // (the pad's x, as terraceLocal reads it)
  const out: Array<NonNullable<Terrace['ramps']>[number]> = [];
  for (const w of roomGraph(layout, new Set(), undefined).ways) {
    if ((w.kind !== 'gate' && w.kind !== 'gateWide') || (w.a !== OUTSIDE && w.b !== OUTSIDE)) continue;
    // its spot and its outward normal (from the inside to the outside) in the world, then in the pad's frame
    const sgn = w.b === OUTSIDE ? 1 : -1;
    const at = vecNormalize({ x: up.x + (tx.x * w.x + tz.x * w.z) * person / R, y: up.y + (tx.y * w.x + tz.y * w.z) * person / R, z: up.z + (tx.z * w.x + tz.z * w.z) * person / R });
    const n = { x: sgn * (tx.x * w.nx + tz.x * w.nz), y: sgn * (tx.y * w.nx + tz.y * w.nz), z: sgn * (tx.z * w.nx + tz.z * w.nz) };
    const ox = n.x * rx + n.y * ry + n.z * rz, oz = n.x * f.x + n.y * f.y + n.z * f.z;
    const lx = ((at.x - d.x) * rx + (at.y - d.y) * ry + (at.z - d.z) * rz) * R, lz = ((at.x - d.x) * f.x + (at.y - d.y) * f.y + (at.z - d.z) * f.z) * R;
    const half = ((MODULE_SPAN[w.kind] ?? 1) * MODULE_CELL / 2 + TERRACE.rampMarginPeople) * person;
    const side: 'x+' | 'x-' | 'z+' | 'z-' = Math.abs(oz) >= Math.abs(ox) ? (oz > 0 ? 'z+' : 'z-') : (ox > 0 ? 'x+' : 'x-');
    const along = side[0] === 'z' ? lx : lz;
    const fit = rampFit(ground, t, side, along, ps) ?? rampFit(ground, t, side, along, ps, true);
    if (fit) out.push({ side, at: along, half, ...fit });
  }
  return out;
}

/**
 * A ramp's length and grade on `ground` (the planet without `t`'s own rule): out along the lane's middle, the last point the
 * ground stands above the ramp's line, at TERRACE.rampBatter — or, where a hill outruns it to the lane's reach (the end a cut
 * face up to the ground: 71.7 (2), 5 people on s71-1's pool, a dead end for wheels), the least grade in 0.05s to
 * TERRACE.rampSteepest whose line meets the ground within it (the steepest, its face the smallest, where none does). Null
 * where the ground never stands above the line (a fill bank, the hardstand: no cut to ramp). `down` (the owner's stuck
 * troops, Nov 2): the mirror onto fill — the last point the ground stands under a line falling from the level, from
 * TERRACE.fillBatter (the bank's own grade) to rampSteepest, its grade negative; null where the ground falls under it only
 * within the band (a hummock's dip at the pad's edge: the band's bank is the ramp there).
 */
function rampFit(ground: PlanetState, t: Terrace, side: 'x+' | 'x-' | 'z+' | 'z-', along: number, ps: ReturnType<typeof personSetOf>, down = false): { length: number; grade?: number } | null {
  const person = PERSON_BODY_HEIGHT * ps.k, R = ground.radius, d = t.dir, f = t.facing;
  const rx = f.y * d.z - f.z * d.y, ry = f.z * d.x - f.x * d.z, rz = f.x * d.y - f.y * d.x;   // (the pad's x, as terraceLocal reads it)
  const padDir = (x: number, z: number) => vecNormalize({ x: d.x + (rx * x + f.x * z) / R, y: d.y + (ry * x + f.y * z) / R, z: d.z + (rz * x + f.z * z) / R });
  const step = 0.25 * person, reach = t.band * TERRACE.rampReachBands, rise: Array<{ e: number; h: number }> = [];
  for (let e = step; e <= reach + 1e-9; e += step) {
    const p = side === 'z+' ? padDir(along, t.halfZ + e) : side === 'z-' ? padDir(along, -t.halfZ - e) : side === 'x+' ? padDir(t.halfX + e, along) : padDir(-t.halfX - e, along);
    rise.push({ e, h: personSurfaceRadius(ground, p, ps) - t.level });
  }
  const lastPast = (grade: number) => { let last = -1; for (const { e, h } of rise) if (down ? h < -grade * e : h > grade * e) last = e; return last; };
  const first: number = down ? TERRACE.fillBatter : TERRACE.rampBatter;
  let last = lastPast(first);
  if (last < 0 || (down && last + step <= t.band + 1e-9)) return null;   // (onto fill: none where the band's own bank meets the ground)
  if (last + step <= reach + 1e-9) return down ? { length: last + step, grade: -first } : { length: last + step };   // (to the first sample at or past the line: where it meets the ground)
  let grade = first;
  for (let k = 1; grade < TERRACE.rampSteepest - 1e-9; k++) {
    grade = Math.min(TERRACE.rampSteepest, first + 0.05 * k);
    last = lastPast(grade);
    if (last + step <= reach + 1e-9) break;
  }
  return { length: Math.min(reach, Math.max(step, last + step)), grade: down ? -grade : grade };
}

/**
 * 71.7 (2) (lane H's finding, the owner's invisible wall at the motor pool's exit): every terrace's ramps refitted once the
 * setup has laid them all — a ramp measured when its pool's terrace was laid (core/motorPoolStock.ts layPoolTerraces) met
 * the ground then, but a terrace laid after it (terraceBuilds) moved a coarse facet the lane shares, and the lane ended
 * 0.5–0.8 people under the ground in a cut face (s71-7). Each ramp keeps its gate, side and width; its length and grade are
 * found again on the planet as it now stands (without the terrace's own rule). The aprons never read the ramps, so the log's
 * records — swapped for the refitted ones — lay the same ground on a replay. The same state where no terrace has a ramp.
 */
export function settleRamps(state: GameState): GameState {
  if (!state.planets.some(p => p.terraces?.some(t => t.ramps?.length))) return state;
  const ps = personSetOf(state), fitted = new Map<string, Terrace>();
  const planets = state.planets.map((planet, pi) => {
    if (!planet.terraces?.some(t => t.ramps?.length)) return planet;
    let p = planet;
    for (const t of planet.terraces) {
      if (!t.ramps?.length) continue;
      const ground = { ...p, terraces: (p.terraces ?? []).filter(x => x.id !== t.id) };
      const ramps = t.ramps.map(r => { const down = (r.grade ?? 0) < 0, fit = rampFit(ground, t, r.side, r.at, ps, down) ?? (down ? { length: r.length, grade: r.grade } : null); return { side: r.side, at: r.at, half: r.half, length: fit?.length ?? r.length, ...(fit?.grade !== undefined ? { grade: fit.grade } : {}) }; });
      const t2: Terrace = { ...t, ramps };
      fitted.set(`${pi}:${t.id}`, t2);
      p = { ...p, terraces: (p.terraces ?? []).map(x => (x.id === t.id ? t2 : x)) };
    }
    return p;
  });
  const terrainEdits = (state.terrainEdits ?? []).map(e => {
    const t2 = e.kind === 'terrace' && e.terrace ? fitted.get(`${e.planetIndex}:${e.terrace.id}`) : undefined;
    return t2 ? { ...e, terrace: t2 } : e;
  });
  return { ...state, planets, terrainEdits };
}

/**
 * The owner's stuck troops (Nov 2): every terrace's fill bank carried on to the ground once the setup has laid them all — at
 * the band's edge the bank (TERRACE.fillBatter) stopped where the ground fell away faster, in a face a person walking off
 * the pad's side met and a vehicle out of a gate onto fill could not leave by (4.3 people under lab-38's pool, 8.7 under
 * s71-2's). Each terrace's `fillBanks` found on the planet as it stands (without its own rule) by fillBanksOf; a planet's
 * pad snapshots taken again (71.5 step 2: each pad's kept mesh reaches as far as its rule), so the log's records — swapped
 * for the banked ones — lay the same ground on a replay. The same state where no bank stops short.
 */
export function settleBanks(state: GameState): GameState {
  if (!state.planets.some(p => p.terraces?.length)) return state;
  const ps = personSetOf(state), banked = new Map<string, Terrace>();
  const planets = state.planets.map((planet, pi) => {
    if (!planet.terraces?.length) return planet;
    let p = planet;
    for (const t of planet.terraces) {
      const banks = fillBanksOf({ ...p, terraces: (p.terraces ?? []).filter(x => x.id !== t.id) }, t, ps);
      if (banks === undefined) continue;
      const t2: Terrace = { ...t, fillBanks: banks };
      banked.set(`${pi}:${t.id}`, t2);
      p = { ...p, terraces: (p.terraces ?? []).map(x => (x.id === t.id ? t2 : x)) };
    }
    return p === planet ? planet : p.shapingOrder ? { ...p, padLaid: padSnapshots(p) } : p;
  });
  if (banked.size === 0) return state;
  const terrainEdits = (state.terrainEdits ?? []).map(e => {
    const t2 = e.kind === 'terrace' && e.terrace ? banked.get(`${e.planetIndex}:${e.terrace.id}`) : undefined;
    return t2 ? { ...e, terrace: t2, radius: terraceReach(t2) } : e;
  });
  return { ...state, planets, terrainEdits };
}

/**
 * Each side's fill bank of `t` — x+, x−, z+, z− (Terrace.fillBanks) — on `ground` (the planet without `t`'s own rule),
 * straight out from the side's edge a person apart and round its corners: the least grade past the band, from
 * TERRACE.fillBatter in 0.05s to TERRACE.rampSteepest (a ramp's), whose line on from the band's edge meets the ground on every
 * ray at one reach, from the band out in quarter people to TERRACE.rampReachBands bands — and the least such reach (past it
 * the rule is the ground, so no face there). Within the band, the band's bank as it was. Where none meets (a ground falling faster than a ramp to there: a cliff below the pad), the grade and reach
 * whose face is the least — carried to the cap, the face would only move out and grow. Undefined where every side's bank
 * meets the ground at the band at the fill's own grade (the band's rule as it was).
 */
export function fillBanksOf(ground: PlanetState, t: Terrace, ps: ReturnType<typeof personSetOf>): Array<{ reach: number; grade: number }> | undefined {
  const person = PERSON_BODY_HEIGHT * ps.k, R = ground.radius, d = t.dir, f = t.facing;
  const rx = f.y * d.z - f.z * d.y, ry = f.z * d.x - f.x * d.z, rz = f.x * d.y - f.y * d.x;   // (the pad's x, as terraceLocal reads it)
  const padDir = (x: number, z: number) => vecNormalize({ x: d.x + (rx * x + f.x * z) / R, y: d.y + (ry * x + f.y * z) / R, z: d.z + (rz * x + f.z * z) / R });
  const step = 0.25 * person, cap = t.band * TERRACE.rampReachBands, reaches: number[] = [];
  for (let e = t.band; e <= cap + 1e-9; e += step) reaches.push(e);
  const out: Array<{ reach: number; grade: number }> = [];
  // each side's rays: a start on its edge and a unit way out (the pad's frame) — along the edge a person apart, then three
  // round each of its corners (22.5°, 45°, 67.5° off it)
  const corners: Array<[number, number]> = [[0.9238795325112867, 0.3826834323650898], [0.7071067811865476, 0.7071067811865476], [0.3826834323650898, 0.9238795325112867]];
  for (let side = 0; side < 4; side++) {
    const onX = side < 2, sg = side % 2 === 0 ? 1 : -1, half = onX ? t.halfZ : t.halfX, edge = onX ? t.halfX : t.halfZ;
    const rays: Array<[number, number, number, number]> = [];
    const n = Math.max(1, Math.ceil((2 * half) / person));
    for (let i = 0; i <= n; i++) { const a = -half + (2 * half * i) / n; rays.push(onX ? [sg * edge, a, sg, 0] : [a, sg * edge, 0, sg]); }
    for (const so of [1, -1]) for (const [c, sn] of corners) rays.push(onX ? [sg * t.halfX, so * t.halfZ, sg * c, so * sn] : [so * t.halfX, sg * t.halfZ, so * sn, sg * c]);
    // the lowest ground over the side's rays at each reach (a line meets them all where it stands at or under it)
    const low = reaches.map(e => { let m = Infinity; for (const [x, z, ux, uz] of rays) m = Math.min(m, groundAt(ground, padDir(x + ux * e, z + uz * e), 'ground', ps)); return m; });
    let best = { reach: t.band, grade: TERRACE.fillBatter as number }, least = Infinity;
    for (let k = 0, grade: number = TERRACE.fillBatter; least > 0; k++) {
      grade = Math.min(TERRACE.rampSteepest, TERRACE.fillBatter + 0.05 * k);
      for (let j = 0; j < reaches.length; j++) {
        const face = t.level - TERRACE.fillBatter * t.band - grade * (reaches[j] - t.band) - low[j];
        if (face < least) { least = face; best = { reach: reaches[j], grade }; }
        if (face <= 0) break;
      }
      if (grade >= TERRACE.rampSteepest - 1e-9) break;
    }
    out.push(best);
  }
  return out.some(b => b.reach > t.band || b.grade !== TERRACE.fillBatter) ? out : undefined;
}

/** The least natural rise under a footing worth a terrace (people): the Lab's dial (GameConfig.terraceRisePeople), else TERRACE.minRisePeople. */
export function terraceMinRise(config: Pick<GameState['config'], 'terraceRisePeople'>): number {
  return config.terraceRisePeople ?? TERRACE.minRisePeople;
}

/** Whether `config` lays terraces (the switch, with the person's fine ground to lay them in). */
export function terracesOn(state: Pick<GameState, 'config'>): boolean {
  return !!state.config.terraces && personSetOf(state).fineAmplitude > 0;
}

/**
 * The natural ground under a footprint, sampled TERRACE.samples a side over the pad (people → world), as the fine ground
 * has it (the mesh, the relief, the digs, any terrace laid before): its rise (max − min) and its median — the level.
 */
export function footprintGround(planet: PlanetState, dir: { x: number; y: number; z: number }, facing: { x: number; y: number; z: number }, across: { x: number; y: number; z: number }, halfX: number, halfZ: number, ps: ReturnType<typeof personSetOf>, at: GroundLevel = 'full'): { rise: number; level: number; samples: number[] } {
  const n = TERRACE.samples, R = planet.radius, samples: number[] = [];
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    const x = (-1 + (2 * i) / (n - 1)) * halfX, z = (-1 + (2 * j) / (n - 1)) * halfZ;
    const d = vecNormalize({ x: dir.x + (across.x * x + facing.x * z) / R, y: dir.y + (across.y * x + facing.y * z) / R, z: dir.z + (across.z * x + facing.z * z) / R });
    samples.push(groundAt(planet, d, at, ps));
  }
  const sorted = samples.slice().sort((a, b) => a - b);
  const m = sorted.length >> 1, level = sorted.length % 2 ? sorted[m] : (sorted[m - 1] + sorted[m]) / 2;
  return { rise: sorted[sorted.length - 1] - sorted[0], level, samples };
}

/**
 * The setup pass: every standing structure with a walkable footing, in order, gets a terrace where its footprint's rise
 * asks for one; each is laid on its planet through the log (the state's `terrainEdits` grows by the record). The same
 * state when none is laid, or the switch is off.
 */
export function terraceBuilds(state: GameState): GameState {
  if (!terracesOn(state)) return state;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  let planets = state.planets, edits = state.terrainEdits ?? [], laid = false;
  let nextId = 1;
  for (const p of planets) for (const t of p.terraces ?? []) nextId = Math.max(nextId, t.id + 1);
  for (const s of state.structures) {
    if (s.health <= 0) continue;
    const foot = footingOf(s, state.config);
    if (!foot) continue;
    const planet = planets[s.planetIndex];
    if (!planet || planet.type !== 'planet' || !planet.alive) continue;
    const up = vecNormalize(s.surfaceDir), { tz } = structureFrame(s, up);
    // (the pad's x axis as terraceLocal reads it: facing × up — the structure's own tx up to sign, which a symmetric footprint does not mind)
    const across = { x: tz.y * up.z - tz.z * up.y, y: tz.z * up.x - tz.x * up.z, z: tz.x * up.y - tz.y * up.x };
    const halfX = foot.halfXPeople * person, halfZ = foot.halfZPeople * person;
    const { rise, level } = footprintGround(planet, up, tz, across, halfX, halfZ, ps);
    if (rise < terraceMinRise(state.config) * person || rise > TERRACE.maxStepPeople * person) continue;
    const t0: Terrace = { id: nextId++, dir: up, facing: tz, halfX, halfZ, band: TERRACE.bandPeople * person, level };
    const ramps = gateRamps(planet, t0, s, state.config, ps), t: Terrace = ramps.length ? { ...t0, ramps } : t0;   // (69.42: its gates' ramps)
    const e = terraceEdit(s.planetIndex, planet, t);
    planets = planets.map((p, i) => (i === s.planetIndex ? applyTerrainEdit(p, e) : p));
    edits = [...edits, e];
    laid = true;
  }
  return laid ? { ...state, planets, terrainEdits: edits } : state;
}

/**
 * A readout for the page's console (main.ts at a match's start, with the switch): the structures terraced, each with
 * the step it made (the level over the coarse ground at its centre, people), and how many footings were read in all — or
 * why none was laid (the switch off; no footing's rise in the window: flat sites keep their bedding, over the cap the
 * plinth stands in). Presentation only.
 */
export function terraceReadout(state: GameState): string {
  if (!terracesOn(state)) return 'off (the Terraces row, or no fine ground)';
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  const laid = (state.terrainEdits ?? []).filter(e => e.kind === 'terrace' && e.terrace);
  const footings = state.structures.filter(s => s.health > 0 && footingOf(s, state.config)).length;
  if (!laid.length) return `none laid under ${footings} footing(s): every site's natural rise under ${terraceMinRise(state.config)} people (the plinth's bedding takes it; the Terrace from row lowers it) or over the ${TERRACE.maxStepPeople}-people step cap`;
  const parts = laid.map(e => {
    const t = e.terrace!, s = state.structures.find(x => Math.abs(x.surfaceDir.x - t.dir.x) + Math.abs(x.surfaceDir.y - t.dir.y) + Math.abs(x.surfaceDir.z - t.dir.z) < 1e-9);
    const planet = state.planets[e.planetIndex];
    const step = planet ? (t.level - getSurfaceRadius(planet, t.dir)) / person : 0;
    return `${s ? `${s.type}#${s.id}` : '?'} (${step >= 0 ? '+' : ''}${step.toFixed(2)} people over the mesh)`;
  });
  return `${laid.length} of ${footings} footing(s): ${parts.join(', ')}`;
}

